/**
 * Custom Theme Hook
 * Utility hook for accessing theme context and theme-aware styling
 */

import { useContext } from 'react';
import ThemeContext from './ThemeContext';

/**
 * Hook to access theme context
 * @returns {object} Theme context with toggle and state
 */
export const useTheme = () => {
  const context = useContext(ThemeContext);
  
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  
  return context;
};

/**
 * Helper function to get theme-aware color
 * @param {string} lightColor - Color for light mode
 * @param {string} darkColor - Color for dark mode
 * @param {boolean} isDarkMode - Current theme mode
 * @returns {string} Appropriate color based on theme
 */
export const getThemeColor = (lightColor, darkColor, isDarkMode) => {
  return isDarkMode ? darkColor : lightColor;
};

/**
 * Helper function to get CSS variable with fallback
 * @param {string} varName - CSS variable name
 * @param {string} fallback - Fallback value
 * @returns {string} CSS variable reference or fallback
 */
export const getCssVar = (varName, fallback) => {
  return `var(${varName}, ${fallback})`;
};

/**
 * Helper to create theme-aware inline styles
 * @param {object} lightStyles - Styles for light mode
 * @param {object} darkStyles - Styles for dark mode
 * @param {boolean} isDarkMode - Current theme mode
 * @returns {object} Merged styles based on current theme
 */
export const getThemeStyles = (lightStyles, darkStyles, isDarkMode) => {
  return isDarkMode ? { ...lightStyles, ...darkStyles } : lightStyles;
};

export default useTheme;
